import {useHistory} from 'react-router-dom';
import React, {Fragment, useEffect, useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Announcements from '../components/header/announcements';
import {agylePref, saveAgylePreference} from '../store/reducers/preferences';
import {Box, Grid} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import {currentMap, updateMap} from '../store/reducers/ward';
import AgyleDialog from '../components/modal/agyleDialog';
import useOpenState from '../hooks/useOpenState';
import {hasPermission, Permissions, user} from '../store/reducers/user';
import {loadingPref} from '../store/reducers/additionalData';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AgyleButton from '../components/ui/buttons/agyleButton';
import InsightsIcon from '@mui/icons-material/Insights';
import StatisticED, {POOLE_STATISTICS, RBH_NEW_STATISTICS, RBH_STATISTICS} from './ward/statisticED';
import AgyleHeader from '../components/header/agyleHeader/agyleHeader';
import MessageBox from '../util/messageBox';
import {useTranslation} from 'react-i18next';
import AgyleDictionaryField from '../components/ui/selector/agyleDictionaryField';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import BedBookingTable from './bedBooking/bedBookingTable';
import {isSpecialSmallDeviceDetect} from '../util/isSpecialSmallDeviceDetect';
import clsx from 'clsx';
import {setSnackbar} from '../store/reducers/globals';
import LineAxisIcon from '@mui/icons-material/LineAxis';
import {getConfig} from '../util/configUtil';
import AdmitPatientNewNew from './admit/admitPatientNewNew';
import {resetAppointments} from '../store/reducers/appointments';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    card: {
        marginLeft: 15,
        marginBottom: 10,
        padding: 10,
        border: 'solid 1px',
        borderColor: theme.palette.background.default,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',

        '&:hover': {
            borderColor: 'grey',
        },
        '&.disabled': {
            backgroundColor: 'grey',
            cursor: 'no-drop',
            '& img': {
                opacity: 0.5,
            },
            '&:hover': {
                borderColor: theme.palette.background.default,
            },
        },
    },
    wardText: {
        textAlign: 'center',
        fontSize: 'large',
        fontStyle: 'italic',
    },
    hospitalName: {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 'x-large',
        bottom: 0,
    },
    map: {
        margin: '15px 0',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '80%',
    },
    button: {
        width: '100%',
        marginTop: 10,
    },
    agyleMenu: {
        paddingTop: 2,
        margin: '0 5px',
    },
    row: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        fontWeight: 'bold',
        fontSize: 'xx-large',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
}));

const MainDashboard = () => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const agylePreference = useSelector(agylePref);
    const userObject = useSelector(user);
    const loading = useSelector(loadingPref);
    const newCurrentMap = useSelector(currentMap);

    const isSmallDevice = isSpecialSmallDeviceDetect();
    const redirect = isSmallDevice ? '/wardIpod/ED' : '/ward/ED';
    const hospitals = getConfig('modules.map.customMapConfig', []);
    const gridSize = 12 / hospitals?.length;

    const [isAdmitOpen, openAdmit, closeAdmit] = useOpenState(false, true);
    const [isSelectDashboardOpen, openSelectDashboard, closeSelectDashboard] = useOpenState(false, true);
    const [isStatisticOpen, openStatistic, closeStatistic] = useOpenState(false, true);
    const [isBedBookingOpen, openBedBooking, closeBedBooking] = useOpenState(false, true);

    const [hospital, setHospital] = useState(null);
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);

    // Permission
    const [canAdmit, setCanAdmit] = useState(false);
    const [canBooking, setCanBooking] = useState(false);

    useEffect(() => {
        document.title = 'Agyle';
        // dispatch(loadUser());
    }, []);

    useLayoutEffect(() => {
        setCanAdmit(dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT)));
        setCanBooking(dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_BED_BOOKING)));
        if (!!agylePreference?.switchMapMode && agylePreference?.switchMapMode !== 'central' && _.isNull(newCurrentMap)) {
            let map = null;
            if (agylePreference?.switchMapMode === 'auto') {
                if (!_.isEmpty(agylePreference?.customMap)) map = agylePreference?.customMap;
            } else {
                map = _.find(hospitals, map => map?.mapPath === agylePreference?.switchMapMode);
            }
            if (map) {
                dispatch(updateMap(map));
                history.push(redirect);
            }
        }

        if (userObject?.userName === 'trainingvideo') {
            history.push('/training');
        }
    }, [agylePreference, userObject]);

    useEffect(() => {
        if (hospital) {
            switch (hospital.mapPath) {
                case 'poole':
                    setZones(_.sortBy(POOLE_STATISTICS, ['title']));
                    break;
                case 'rbh':
                    setZones(_.sortBy(RBH_STATISTICS, ['title']));
                    break;
                case 'rbh_new':
                    setZones(_.sortBy(RBH_NEW_STATISTICS, ['title']));
                    break;
                default:
                    setZones([]);
                    break;
            }
        }
    }, [hospital]);

    const handleClick = (obj, canAccess) => {
        if (canAccess) {
            dispatch(saveAgylePreference({customMap: obj}));
            dispatch(updateMap(obj));
            dispatch(resetAppointments())
            history.push(redirect);
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('User has no permission to access this hospital!')}));
        }
    };

    const handleCloseAdmit = () => {
        setHospital(null);
        closeAdmit();
    };

    const handleCloseStatistic = () => {
        setHospital(null);
        setSelectedZone(null);
        closeStatistic();
    };

    const handleOpenReport = () => {
        history.push('/report/dashboard');
    };

    const renderCard = (hospitalEl) => {
        const path = `/maps/${hospitalEl.mapPath}/ed_geo.svg`;
        const canAccess = dispatch(hasPermission(hospitalEl?.mapPath === 'poole' ? Permissions.AGYLE_CAN_ACCESS_PH : Permissions.AGYLE_CAN_ACCESS_RBH));

        return (
            <Grid
                key={hospitalEl.name}
                item
                xs={12}
                sm={5}
                className={clsx(classes.card, !canAccess && 'disabled')}
                onClick={handleClick.bind(this, hospitalEl, canAccess)}
            >
                <div className={classes.wardText}>
                    Ward Map ED
                </div>

                <Box sx={{display: {sm: 'block', xs: 'none'}}}>
                    <img src={path} alt={hospitalEl.name + ' Map'} width={'auto'} height={230} className={classes.map}/>
                </Box>

                <div className={classes.hospitalName}>
                    {hospitalEl.name}
                </div>
            </Grid>
        );
    };

    const handleOpenAdmit = (id) => {
        const hospitalObj = _.find(hospitals, {mapPath: id});
        if (hospitalObj) {
            setHospital(hospitalObj);
            openAdmit();
        }
    };

    const handleOpenStatistic = (id) => {
        const hospitalObj = _.find(hospitals, {mapPath: id});
        if (hospitalObj) {
            setHospital(hospitalObj);
            openSelectDashboard();
        }
    };

    const handleOpenEWTStatistic = (id) => {
        const hospitalObj = _.find(hospitals, {mapPath: id});
        if (hospitalObj) {
            setHospital(hospitalObj);
            history.push(`report/estimatedTime/${hospitalObj?.hospitalId}`);
        }
    };

    const handleSelectDashboard = () => {
        if (selectedZone) {
            closeSelectDashboard();
            openStatistic();
        }
    };

    const handleCloseSelectDashboard = () => {
        setSelectedZone(null);
        closeSelectDashboard();
    };

    const renderSelect = () => {
        const handleChangeZone = (_e, val) => {
            setSelectedZone(val);
        };

        return (
            hospital &&
            <div style={{minWidth: 300}}>
                <AgyleDictionaryField
                    onChange={handleChangeZone}
                    value={selectedZone}
                    options={zones}
                    getOptionLabel={option => option.title}
                    label={t('Select Zone/Bay')}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                />
            </div>
        );
    };

    return (!loading &&
        <Fragment>
            <Announcements/>

            <AgyleHeader disableBack/>

            <Grid container direction="row" justifyContent="space-around" sx={{mt: 1}}>
                {hospitals.map(el => (
                    renderCard(el)
                ))}
            </Grid>

            {canAdmit &&
                <Grid container direction="row" spacing={1} style={{padding: 5}}>
                    {hospitals.map(el => (
                        <Grid
                            item
                            xs={12}
                            sm={gridSize || 6}
                            key={el.mapPath}
                        >
                            <AgyleButton
                                size="large"
                                className={classes.button}
                                onClick={handleOpenAdmit.bind(this, el.mapPath)}
                                startIcon={<PersonAddIcon/>}
                                width="100%"
                            >
                                {t('Create New Attendance - ')}{el.name}
                            </AgyleButton>
                        </Grid>
                    ))}
                </Grid>
            }

            <Grid container direction="row" spacing={1} style={{padding: 5}}>
                {hospitals.map(el => (
                    <Grid
                        item
                        xs={12}
                        sm={gridSize || 6}
                        key={el.mapPath}
                    >
                        <AgyleButton
                            size="large"
                            className={classes.button}
                            onClick={handleOpenStatistic.bind(this, el.mapPath)}
                            startIcon={<InsightsIcon/>}
                            width="100%"
                        >
                            {t('ED Treatment Dashboard - ')}{el.name}
                        </AgyleButton>
                    </Grid>
                ))}
            </Grid>

            <Grid container direction="row" spacing={1} style={{padding: 5}}>
                {hospitals.map(el => (
                    <Grid
                        item
                        xs={12}
                        sm={gridSize || 6}
                        key={el.mapPath}
                    >
                        <AgyleButton
                            size="large"
                            className={classes.button}
                            onClick={handleOpenEWTStatistic.bind(this, el.mapPath)}
                            startIcon={<InsightsIcon/>}
                            width="100%"
                        >
                            {t('Live Wait Dashboard - ')}{el.name}
                        </AgyleButton>
                    </Grid>
                ))}
            </Grid>

            {canBooking &&
                <Grid
                    item
                    xs={12}
                    style={{padding: 5}}
                >
                    <AgyleButton
                        size="large"
                        className={classes.button}
                        onClick={openBedBooking}
                        width="100%"
                        startIcon={<HotelOutlinedIcon/>}
                    >
                        {t('Bed Booking')}
                    </AgyleButton>
                </Grid>
            }

            <Grid
                item
                xs={12}
                style={{padding: 5}}
            >
                <AgyleButton
                    size="large"
                    className={classes.button}
                    onClick={handleOpenReport}
                    width="100%"
                    startIcon={<LineAxisIcon/>}
                >
                    {t('Emergency Department Live Dashboard')}
                </AgyleButton>
            </Grid>

            {canAdmit &&
                <AgyleDialog
                    title={t('Admit Patient to ED')}
                    open={isAdmitOpen}
                    onClose={handleCloseAdmit}
                    fullScreen
                    noPadding
                    onlyModal
                    disableSave
                    darkContentBackground
                >
                    <AdmitPatientNewNew
                        disableHeader
                        hospitalObj={hospital}
                    />
                </AgyleDialog>
            }

            <AgyleDialog
                open={isStatisticOpen}
                onClose={handleCloseStatistic}
                fullScreen
                disableSave
                onlyModal
                noPadding
                contentStyle={classes.background}
            >
                <StatisticED
                    zone={selectedZone}
                    hospital={hospital}
                    open={isStatisticOpen}
                    onClose={handleCloseStatistic}
                />
            </AgyleDialog>

            {canBooking &&
                <AgyleDialog
                    open={isBedBookingOpen}
                    onClose={closeBedBooking}
                    fullScreen
                    onlyModal
                >
                    <BedBookingTable
                        open={isBedBookingOpen}
                        onClose={closeBedBooking}
                    />
                </AgyleDialog>
            }

            <MessageBox
                title={t('Select Dashboard')}
                haveAccept={{yes: t('Select'), no: t('Close')}}
                onAccept={handleSelectDashboard}
                onClose={handleCloseSelectDashboard}
                messageObject={renderSelect()}
                open={isSelectDashboardOpen}
            />
        </Fragment>
    );
};

export default MainDashboard;